import { useCallback, useMemo, useState } from 'react';
import { Box, Button, DropdownSelect, Heading, Modal } from '@zillow/constellation-10';
import type { YourRentalPropertyDto } from '@zg-rentals/ts-your-rental-api-client';

const CONTENT = {
  TITLE: 'Your rentals',
  VIEW_BUTTON: 'View',
  VIEW_RENTALS_BUTTON: 'View your rentals',
  NEW_RENTAL: 'New rental',
} as const;

type ViewRentalsModalProps = {
  yourRentalProperties: Array<YourRentalPropertyDto>;
  selectedProperty: YourRentalPropertyDto;
  onPropertyChange: (property: YourRentalPropertyDto) => void;
};

const getPropertyId = (property: YourRentalPropertyDto, index: number): string => {
  if (property.listingSection?.listingAlias) {
    return property.listingSection.listingAlias;
  }

  return `property-${index}`;
};

const formatAddress = (property: YourRentalPropertyDto): string => {
  if (!property.listingSection || !property.listingSection.address) {
    return CONTENT.NEW_RENTAL;
  }
  const { street, city, state, zip, unit } = property.listingSection.address;
  return `${street}${unit ? ` #${unit}` : ''}, ${city}, ${state} ${zip}`;
};

const ViewRentalsModal = ({ yourRentalProperties, selectedProperty, onPropertyChange }: ViewRentalsModalProps) => {
  const propertyMap = useMemo(() => {
    const map = new Map<string, YourRentalPropertyDto>();
    yourRentalProperties.forEach((property, index) => {
      const propertyId = getPropertyId(property, index);
      map.set(propertyId, property);
    });
    return map;
  }, [yourRentalProperties]);

  const dropdownOptions = useMemo(() => {
    return yourRentalProperties.map((property, index) => ({
      value: getPropertyId(property, index),
      label: formatAddress(property),
    }));
  }, [yourRentalProperties]);

  const [tempSelectedValue, setTempSelectedValue] = useState(() => {
    const selectedIndex = yourRentalProperties.indexOf(selectedProperty);
    return getPropertyId(selectedProperty, selectedIndex >= 0 ? selectedIndex : 0);
  });

  const handleDropdownChange = useCallback((value: string) => {
    setTempSelectedValue(value);
  }, []);

  const handleViewClick = useCallback(() => {
    const property = propertyMap.get(tempSelectedValue);
    if (property) {
      onPropertyChange(property);
    }
  }, [propertyMap, tempSelectedValue, onPropertyChange]);

  const renderBody = useCallback(() => {
    return (
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 'default',
        }}
        data-testid="ViewRentalsModal-Body"
      >
        <Heading data-testid="ViewRentalsModal-Header" level={2} textStyle="heading-sm">
          {CONTENT.TITLE}
        </Heading>
        {dropdownOptions.length > 0 && (
          <DropdownSelect
            options={dropdownOptions}
            value={tempSelectedValue}
            onChange={handleDropdownChange}
            showLabelForValue
            css={{ width: '100%' }}
            data-testid="ViewRentalsModal-Dropdown"
            aria-label="Select rental property"
          />
        )}
      </Box>
    );
  }, [dropdownOptions, tempSelectedValue, handleDropdownChange]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        const selectedIndex = yourRentalProperties.indexOf(selectedProperty);
        setTempSelectedValue(getPropertyId(selectedProperty, selectedIndex >= 0 ? selectedIndex : 0));
      }
    },
    [selectedProperty, yourRentalProperties],
  );

  return (
    <Modal
      trigger={
        <Button data-testid="Header-ViewRentalsButton" size="sm" aria-label="View all your rental properties">
          {CONTENT.VIEW_RENTALS_BUTTON}
        </Button>
      }
      size="md"
      scrollDocument
      body={renderBody()}
      footer={
        <Modal.Close>
          <Button
            data-testid="ViewRentalsModal-FooterButton"
            emphasis="filled"
            size="md"
            onClick={handleViewClick}
            aria-label="View selected rental property"
          >
            {CONTENT.VIEW_BUTTON}
          </Button>
        </Modal.Close>
      }
      onOpenChange={handleOpenChange}
    />
  );
};

export default ViewRentalsModal;
